import React, { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { money } from "@/data/site";
import { Package, LogOut, User as UserIcon, Truck } from "lucide-react";

export default function Profile() {
  const { user, logout } = useApp();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    if (user) api.get("/orders/mine").then((r) => setOrders(r.data)).catch(() => {});
  }, [user]);

  if (user === null) return <div className="py-40 text-center text-cream/50">Loading…</div>;
  if (user === false) return <Navigate to="/login" replace />;

  return (
    <div className="mx-auto max-w-5xl px-4 py-12 sm:px-8" data-testid="profile-page">
      <div className="mb-10 flex flex-col justify-between gap-4 border-b border-white/10 pb-8 sm:flex-row sm:items-end">
        <div>
          <p className="overline-label mb-2">My Account</p>
          <h1 className="font-black uppercase tracking-tight text-cream text-3xl sm:text-4xl">{user.name}</h1>
          <p className="mt-1 text-sm text-cream/50">{user.email}</p>
        </div>
        <button onClick={() => { logout(); navigate("/"); }} data-testid="logout-button" className="flex items-center gap-2 border border-white/15 px-5 py-3 text-xs uppercase tracking-widest text-cream hover:border-gold hover:text-gold">
          <LogOut size={14} /> Sign Out
        </button>
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        <Stat icon={<Package size={18} />} label="Orders" value={orders.length} />
        <Stat icon={<UserIcon size={18} />} label="Total Spent" value={money(orders.reduce((s, o) => s + o.total, 0))} />
        <Stat icon={<UserIcon size={18} />} label="Member Since" value={new Date(user.created_at || Date.now()).getFullYear()} />
      </div>

      <h2 className="mb-6 mt-14 font-serif text-2xl text-cream">Order History</h2>
      {orders.length === 0 ? (
        <p className="border border-white/10 py-16 text-center text-sm text-cream/50" data-testid="no-orders">No orders yet — your future looks golden.</p>
      ) : (
        <div className="space-y-4" data-testid="order-history">
          {orders.map((o) => (
            <div key={o.id} className="border border-white/10 bg-ink p-5" data-testid={`order-${o.order_number}`}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="font-mono text-sm text-gold">{o.order_number}</p>
                  <p className="text-xs text-cream/40">{new Date(o.created_at).toLocaleDateString()}</p>
                </div>
                <span className="border border-gold/40 px-3 py-1 text-[0.6rem] uppercase tracking-widest text-gold capitalize">{o.status}</span>
                <span className="font-mono text-cream">{money(o.total)}</span>
              </div>
              {o.tracking_url && (
                <a href={o.tracking_url} target="_blank" rel="noopener noreferrer" data-testid={`track-${o.order_number}`}
                  className="mt-3 inline-flex items-center gap-2 text-xs uppercase tracking-widest text-gold hover:text-gold-bright">
                  <Truck size={14} /> Track your shipment{o.tracking_code ? ` · ${o.tracking_code}` : ""}
                </a>
              )}
              <div className="mt-4 flex flex-wrap gap-3 border-t border-white/10 pt-4">
                {o.items.map((it, i) => (
                  <div key={`${it.variant_id}-${i}`} className="flex items-center gap-2">
                    <img src={it.image} alt={it.product_name} className="h-12 w-10 object-cover border border-white/10" />
                    <div className="text-xs"><p className="text-cream">{it.product_name}</p><p className="text-cream/40">{it.color}/{it.size} ×{it.quantity}</p></div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Stat({ icon, label, value }) {
  return (
    <div className="border border-white/10 bg-ink p-5">
      <div className="mb-2 text-gold">{icon}</div>
      <p className="overline-label text-[0.6rem]">{label}</p>
      <p className="mt-1 font-mono text-xl text-cream">{value}</p>
    </div>
  );
}
